<template>
    <div class="screen-row">
        <div class="checkbox-item" :class="rowClass">
            <input
                :id="inputId"
                type="checkbox"
                class="custom-checkbox"
                :checked="checked"
                :disabled="disabled"
                @change="emit('toggle')"
            />
            <label :for="inputId" class="checkbox" :class="{ disabled }">
                <span class="checkbox__label">
                    <span class="checkbox__name" :title="title">
                        <button
                            type="button"
                            class="screen-locate"
                            aria-label="Показать на карте"
                            @click.stop.prevent="emit('focus')"
                        >
                            <img src="/images/marker-chb.svg" alt="" />
                        </button>
                        <span class="frame-id-badge">{{ frame.gid }}</span>
                        <ScreenRequestSlot
                            v-if="showWeekSlot"
                            :active="weekActive"
                            :details="weekDetails"
                            :expanded="weekExpanded"
                            @toggle="emit('toggle-week')"
                        />
                        <button
                            v-else-if="showLegacyWarning"
                            type="button"
                            class="screen-warning"
                            :aria-label="legacyWarning"
                            v-tippy="{ content: legacyWarning, theme: 'light' }"
                            @click.prevent.stop
                        >
                            <img src="/images/warning.svg" alt="" />
                        </button>
                        <img
                            v-if="frame.has_beeline"
                            src="/images/audience.svg"
                            class="screen-audience"
                            alt="Аудиторные данные"
                            title="Аудиторные данные"
                        />
                        <span class="checkbox__title">{{ displayLabel }}</span>
                    </span>
                    <span class="checkbox__meta">
                        <span v-if="frame.width && frame.height" class="frame-size">
                            ({{ frame.width }}×{{ frame.height }})
                        </span>
                        <span v-if="frame.is_test" class="test-badge">Тест</span>
                        <span v-if="showInactiveBadge" class="inactive-badge">Нет цен</span>
                        <span v-if="packageName" class="package-badge">{{ packageName }}</span>
                    </span>
                </span>
            </label>
        </div>
        <div v-if="weekOpen" class="screen-row__week">
            <ScreenRequestWeek :gid="String(frame.gid)" />
        </div>
    </div>
</template>

<script setup>
import ScreenRequestSlot from './ScreenRequestSlot.vue';
import ScreenRequestWeek from './ScreenRequestWeek.vue';

defineProps({
    frame: { type: Object, required: true },
    inputId: { type: String, required: true },
    checked: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    title: { type: String, default: '' },
    displayLabel: { type: String, default: '' },
    rowClass: { type: Object, default: () => ({}) },
    showWeekSlot: { type: Boolean, default: false },
    weekActive: { type: Boolean, default: false },
    weekDetails: { type: Boolean, default: true },
    weekExpanded: { type: Boolean, default: false },
    weekOpen: { type: Boolean, default: false },
    showLegacyWarning: { type: Boolean, default: false },
    legacyWarning: { type: String, default: '' },
    showInactiveBadge: { type: Boolean, default: false },
    packageName: { type: String, default: '' },
});

const emit = defineEmits(['toggle', 'focus', 'toggle-week']);
</script>

<style lang="scss" scoped>
.screen-row {
    min-width: 0;
}

.screen-row__week {
    min-width: 0;
    padding: 0 8px 8px;
}

.checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 6px;
    overflow: hidden;
    box-sizing: border-box;

    &:hover {
        background: #f8fafc;
    }

    &.inactive {
        opacity: 0.6;

        &:hover {
            background: #f1f5f9;
        }
    }

    &.ssp-filtered {
        opacity: 0.5;

        .checkbox__name {
            color: #9ca3af;
        }
    }

    &.in-package {
        background: linear-gradient(90deg, rgba(37, 99, 235, 0.03), transparent);
    }
}

.custom-checkbox {
    -webkit-appearance: none;
    appearance: none;
    display: inline-block;
    position: relative;
    width: 18px;
    height: 18px;
    border: 2px solid #cbd5e1;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.18s ease;

    &:checked {
        background: linear-gradient(180deg, #2563eb, #1d4ed8);
        border-color: transparent;
    }

    &:disabled {
        background: #f1f5f9;
        border-color: #e2e8f0;
        cursor: not-allowed;
    }

    &:checked:disabled {
        background: linear-gradient(180deg, #2563eb, #1d4ed8);
        border-color: transparent;
    }
}

.checkbox {
    cursor: pointer;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;

    &.disabled {
        cursor: not-allowed;
        color: #9ca3af;
    }
}

.checkbox__label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-width: 0;
}

.checkbox__name {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    overflow: hidden;
}

.checkbox__title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.checkbox__meta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.frame-size {
    color: #6b7280;
    font-size: 12px;
    margin-left: 4px;
    flex-shrink: 0;
}

.inactive-badge {
    background: #fef3c7;
    color: #92400e;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 500;
    margin-left: 8px;
}

.screen-warning {
    display: inline-flex;
    flex-shrink: 0;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: help;
    line-height: 0;

    img {
        display: block;
        height: 12px;
        width: 12px;
    }
}

.screen-audience {
    display: block;
    flex-shrink: 0;
    width: 13px;
    height: 13px;
}

.package-badge {
    margin-left: 8px;
    font-size: 12px;
    color: #2563eb;
    background: #eff6ff;
    padding: 2px 6px;
    border-radius: 6px;
    font-weight: 600;
}

.screen-locate {
    display: inline-flex;
    flex-shrink: 0;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    line-height: 0;

    img {
        width: 16px;
        height: 16px;
        display: block;
    }
}

.frame-id-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    background: #f3f4f6;
    color: #4b5563;
    white-space: nowrap;
    flex-shrink: 0;
}

.test-badge {
    background: #ede9fe;
    color: #5b21b6;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
    margin-left: 8px;
    white-space: nowrap;
}
</style>
